import type { OptimizelyDatafile } from '#types/p13n'
import {
  createBatchEventProcessor,
  createInstance,
  createOdpManager,
  createPollingProjectConfigManager,
} from '@optimizely/optimizely-sdk'

type OptimizelyClient = ReturnType<typeof createInstance>
declare module '#app' {
  interface NuxtApp {
    $optimizely: {
      client: OptimizelyClient
      dataReady: (userEventData: any) => void
      isAttributeUsedByFlag: (flagKey: string, attributeKey: string) => boolean
    }
  }
}

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $optimizely: {
      client: OptimizelyClient
      dataReady: (userEventData: any) => void
      isAttributeUsedByFlag: (flagKey: string, attributeKey: string) => boolean
    }
  }
}

export default defineNuxtPlugin({
  name: 'optimizely',
  setup: async () => {
    const {
      configP13n: { optimizelySdkKey, enableOptimizely }
    } = useFeatureFlags()

    if (!enableOptimizely) return {}

    const datafileUrl = `https://cdn.optimizely.com/datafiles/${optimizelySdkKey}.json`

    let datafile: OptimizelyDatafile
    const attributesUsedByFlags = new Map<string, Set<string>>()

    const userId = useCookie('optimizelyUserId', {
      maxAge: 60 * 60 * 24 * 365
    })
    const optimizelyVisitNumber = useCookie('optimizelyVisitNumber', {
      default: () => 0,
      encode: (value) => String(value),
      decode: (value) => Number(value),
      maxAge: 60 * 60 * 24 * 90
    })

    const optimizelyActiveVisit = useCookie('optimizelyActiveVisit', {
      default: () => undefined,
      encode: (value) => String(value),
      decode: (value) => value === 'true',
    })

    const optimizelyLandingPage = useCookie('optimizelyLandingPage', {
      default: () => undefined,
      encode: (value) => String(value),
      decode: (value) => value === 'true',
    })

    if (isClient()) {
      if (!optimizelyActiveVisit.value) {
        optimizelyVisitNumber.value++
        optimizelyActiveVisit.value = true
        optimizelyLandingPage.value = true
      }
      else {
        optimizelyLandingPage.value = false
      }

      userId.value ??= uuid()
    }

    let client: OptimizelyClient | null = null

    try {
      const projectConfigManager = createPollingProjectConfigManager({
        sdkKey: optimizelySdkKey,
        autoUpdate: true,
        updateInterval: 300000,
      })

      const eventProcessor = createBatchEventProcessor({
        batchSize: 10,
        flushInterval: 1000,
      })

      const odpManager = createOdpManager({
        segmentsCacheSize: 100,
        segmentsCacheTimeout: 600000,
      })

      client = createInstance({
        projectConfigManager,
        eventProcessor,
        odpManager
      })
      datafile = await $fetch<OptimizelyDatafile>(datafileUrl)
    }
    catch (err) {
      console.warn('[Optimizely] Failed to fetch datafile:', err)
    }

    const dataReady = (userEventData) => {
      if (!isClient()) return
      const auth = useAuthStore()
      window.userLoggedIn = auth?.loggedIn || false
      window.dispatchEvent(new Event('vfUserDataAvailable'))
      if (userEventData) {
        console.info('vfUserDataAvailable event fired')
      }
    }

    const extractAttributesFromConditions = (conditions: OptimizelyDatafile['typedAudiences'][number]['conditions']): Set<string> => {
      const attrs = new Set<string>()
      if (Array.isArray(conditions)) {
        for (const condition of conditions) {
          if (Array.isArray(condition)) {
            extractAttributesFromConditions(condition).forEach((a) => attrs.add(a))
          }
          else if (typeof condition === 'object' && condition !== null) {
            if ('name' in condition) {
              attrs.add(condition.name)
            }
          }
        }
      }
      return attrs
    }

    const isAttributeUsedByFlag = (flagKey: string, attributeKey: string): boolean => {
      log.info(`Optimizely: Checking if attribute "${attributeKey}" is used by flag "${flagKey}"`)
      if (!client || !datafile) return false
      if (attributesUsedByFlags.has(flagKey)) {
        return attributesUsedByFlags.get(flagKey)!.has(attributeKey)
      }

      const rolloutId = datafile.featureFlags.find(({ key }) => key === flagKey)?.rolloutId
      const rollout = (rolloutId && datafile.rollouts.find(({ id }) => id === rolloutId)) || null
      const audienceIds = rollout?.experiments.flatMap(({ audienceIds: ids }) => ids) || []
      const audiences = datafile.typedAudiences.filter(({ id }) => audienceIds.includes(id))
      const attributeKeys = new Set(
        audiences.flatMap(({ conditions }) => [
          ...extractAttributesFromConditions(conditions)
        ])
      )
      attributesUsedByFlags.set(flagKey, attributeKeys)
      return attributeKeys.has(attributeKey)
    }

    return {
      provide: {
        optimizely: {
          client,
          dataReady,
          isAttributeUsedByFlag
        }
      }
    }
  }
})
